import { forwardRef } from 'react';
import { Box, BoxProps } from './box';

/**
 * Section component - Semantic wrapper for content sections.
 *
 * Renders as an HTML `<section>` element for accessibility and SEO.
 * Represents a standalone section of content that typically has a heading.
 *
 * Use `<section>` when the content represents a thematic grouping,
 * typically with a heading. It's more semantic than a generic `<div>`.
 *
 * @example
 * // Content section with heading
 * <Section p={3}>
 *   <Typography variant="h2">Features</Typography>
 *   <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
 *     <FeatureCard />
 *     <FeatureCard />
 *     <FeatureCard />
 *   </Box>
 * </Section>
 *
 * @example
 * // Section with background
 * <Section bgcolor="grey.50" py={4}>
 *   <Container>
 *     <Typography variant="h3" mb={2}>About Us</Typography>
 *     <Typography>Description...</Typography>
 *   </Container>
 * </Section>
 *
 * @example
 * // Accessible section with ARIA label
 * <Section aria-labelledby="contact-heading" p={2}>
 *   <Typography id="contact-heading" variant="h2">Contact</Typography>
 *   <ContactForm />
 * </Section>
 */
export type SectionProps = Omit<BoxProps, 'component'>;

export const Section = forwardRef<HTMLElement, SectionProps>((props, ref) => (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <Box ref={ref as any} component="section" {...props} />
));

Section.displayName = 'Section';
